// Admin-side mirror of apps/public/lib/sidebar-icons.tsx — same keys, same
// order, but rendered through Tabler instead of Lucide (apps/client only
// has Tabler). The two files MUST keep their `key` columns in sync; the
// public renderer is authoritative for the actual visual on the docs site.
import type { ComponentType } from 'react';
import {
  IconHome,
  IconBook,
  IconFile,
  IconFolder,
  IconFolderOpen,
  IconLink,
  IconStar,
  IconHeart,
  IconSettings,
  IconInfoCircle,
  IconHelp,
  IconMessage,
  IconCalendar,
  IconClock,
  IconSearch,
  IconDownload,
  IconUpload,
  IconMail,
  IconPhone,
  IconMapPin,
  IconWorld,
  IconBell,
  IconBookmark,
  IconTag,
  IconStack2,
  IconCode,
  IconTerminal,
  IconPhoto,
  IconVideo,
  IconMusic,
  IconRocket,
  IconBolt,
  IconSparkles,
  IconBulb,
  IconFlame,
  IconTarget,
  IconTrophy,
  IconAward,
  IconDiamond,
  IconCrown,
  IconShield,
  IconLock,
  IconKey,
  IconUser,
  IconUsers,
  IconShoppingCart,
  IconCreditCard,
  IconWallet,
  IconChartBar,
  IconChartPie,
  IconTrendingUp,
  IconActivity,
  IconDatabase,
  IconServer,
  IconCloud,
  IconCpu,
  IconDeviceGamepad2,
  IconMap,
  IconCompass,
  IconNews,
  IconBooks,
  IconSchool,
  IconScript,
  IconTool,
  IconSword,
  IconHammer,
  IconBrandGithub,
  IconBrandX,
  IconBrandYoutube,
  IconBrandDiscord,
  IconBrandTelegram,
  IconBrandSlack,
  IconBrandGitlab,
  IconBrandFigma,
  IconBrandLinkedin,
  IconBrandInstagram,
} from '@tabler/icons-react';

export type IconGroup = 'basic' | 'category' | 'brand';

export interface SidebarIconDef {
  key: string;
  label: string;
  component: ComponentType<{ size?: number | string }>;
  group: IconGroup;
}

export const SIDEBAR_ICONS: SidebarIconDef[] = [
  // Basic
  { key: 'home',        label: 'Home',           component: IconHome,         group: 'basic' },
  { key: 'book',        label: 'Book',           component: IconBook,         group: 'basic' },
  { key: 'file',        label: 'File',           component: IconFile,         group: 'basic' },
  { key: 'folder',      label: 'Folder',         component: IconFolder,       group: 'basic' },
  { key: 'folder-open', label: 'Folder Open',    component: IconFolderOpen,   group: 'basic' },
  { key: 'link',        label: 'Link',           component: IconLink,         group: 'basic' },
  { key: 'star',        label: 'Star',           component: IconStar,         group: 'basic' },
  { key: 'heart',       label: 'Heart',          component: IconHeart,        group: 'basic' },
  { key: 'settings',    label: 'Settings',       component: IconSettings,     group: 'basic' },
  { key: 'info',        label: 'Info',           component: IconInfoCircle,   group: 'basic' },
  { key: 'help',        label: 'Help',           component: IconHelp,         group: 'basic' },
  { key: 'chat',        label: 'Chat',           component: IconMessage,      group: 'basic' },
  { key: 'calendar',    label: 'Calendar',       component: IconCalendar,     group: 'basic' },
  { key: 'clock',       label: 'Clock',          component: IconClock,        group: 'basic' },
  { key: 'search',      label: 'Search',         component: IconSearch,       group: 'basic' },
  { key: 'download',    label: 'Download',       component: IconDownload,     group: 'basic' },
  { key: 'upload',      label: 'Upload',         component: IconUpload,       group: 'basic' },
  { key: 'mail',        label: 'Mail',           component: IconMail,         group: 'basic' },
  { key: 'phone',       label: 'Phone',          component: IconPhone,        group: 'basic' },
  { key: 'map-pin',     label: 'Map Pin',        component: IconMapPin,       group: 'basic' },
  { key: 'globe',       label: 'Globe',          component: IconWorld,        group: 'basic' },
  { key: 'bell',        label: 'Bell',           component: IconBell,         group: 'basic' },
  { key: 'bookmark',    label: 'Bookmark',       component: IconBookmark,     group: 'basic' },
  { key: 'tag',         label: 'Tag',            component: IconTag,          group: 'basic' },
  { key: 'layers',      label: 'Layers',         component: IconStack2,       group: 'basic' },
  { key: 'code',        label: 'Code',           component: IconCode,         group: 'basic' },
  { key: 'terminal',    label: 'Terminal',       component: IconTerminal,     group: 'basic' },
  { key: 'image',       label: 'Image',          component: IconPhoto,        group: 'basic' },
  { key: 'video',       label: 'Video',          component: IconVideo,        group: 'basic' },
  { key: 'music',       label: 'Music',          component: IconMusic,        group: 'basic' },

  // Categories
  { key: 'rocket',      label: 'Rocket',         component: IconRocket,       group: 'category' },
  { key: 'zap',         label: 'Zap',            component: IconBolt,         group: 'category' },
  { key: 'sparkles',    label: 'Sparkles',       component: IconSparkles,     group: 'category' },
  { key: 'lightbulb',   label: 'Idea',           component: IconBulb,         group: 'category' },
  { key: 'flame',       label: 'Flame',          component: IconFlame,        group: 'category' },
  { key: 'target',      label: 'Target',         component: IconTarget,       group: 'category' },
  { key: 'trophy',      label: 'Trophy',         component: IconTrophy,       group: 'category' },
  { key: 'award',       label: 'Award',          component: IconAward,        group: 'category' },
  { key: 'gem',         label: 'Gem',            component: IconDiamond,      group: 'category' },
  { key: 'crown',       label: 'Crown',          component: IconCrown,        group: 'category' },
  { key: 'shield',      label: 'Shield',         component: IconShield,       group: 'category' },
  { key: 'lock',        label: 'Lock',           component: IconLock,         group: 'category' },
  { key: 'key',         label: 'Key',            component: IconKey,          group: 'category' },
  { key: 'user',        label: 'User',           component: IconUser,         group: 'category' },
  { key: 'users',       label: 'Users',          component: IconUsers,        group: 'category' },
  { key: 'cart',        label: 'Cart',           component: IconShoppingCart, group: 'category' },
  { key: 'card',        label: 'Card',           component: IconCreditCard,   group: 'category' },
  { key: 'wallet',      label: 'Wallet',         component: IconWallet,       group: 'category' },
  { key: 'chart-bar',   label: 'Bar chart',      component: IconChartBar,     group: 'category' },
  { key: 'chart-pie',   label: 'Pie chart',      component: IconChartPie,     group: 'category' },
  { key: 'trending',    label: 'Trending',       component: IconTrendingUp,   group: 'category' },
  { key: 'activity',    label: 'Activity',       component: IconActivity,     group: 'category' },
  { key: 'database',    label: 'Database',       component: IconDatabase,     group: 'category' },
  { key: 'server',      label: 'Server',         component: IconServer,       group: 'category' },
  { key: 'cloud',       label: 'Cloud',          component: IconCloud,        group: 'category' },
  { key: 'cpu',         label: 'CPU',            component: IconCpu,          group: 'category' },
  { key: 'gamepad',     label: 'Gamepad',        component: IconDeviceGamepad2, group: 'category' },
  { key: 'map',         label: 'Map',            component: IconMap,          group: 'category' },
  { key: 'compass',     label: 'Compass',        component: IconCompass,      group: 'category' },
  { key: 'news',        label: 'News',           component: IconNews,         group: 'category' },
  { key: 'library',     label: 'Library',        component: IconBooks,        group: 'category' },
  { key: 'graduation',  label: 'Graduation',     component: IconSchool,       group: 'category' },
  { key: 'scroll',      label: 'Scroll',         component: IconScript,       group: 'category' },
  { key: 'wrench',      label: 'Wrench',         component: IconTool,         group: 'category' },
  { key: 'sword',       label: 'Sword',          component: IconSword,        group: 'category' },
  { key: 'hammer',      label: 'Hammer',         component: IconHammer,       group: 'category' },

  // Brand
  { key: 'github',      label: 'GitHub',         component: IconBrandGithub,    group: 'brand' },
  { key: 'x',           label: 'X / Twitter',    component: IconBrandX,         group: 'brand' },
  { key: 'youtube',     label: 'YouTube',        component: IconBrandYoutube,   group: 'brand' },
  { key: 'discord',     label: 'Discord',        component: IconBrandDiscord,   group: 'brand' },
  { key: 'telegram',    label: 'Telegram',       component: IconBrandTelegram,  group: 'brand' },
  { key: 'slack',       label: 'Slack',          component: IconBrandSlack,     group: 'brand' },
  { key: 'gitlab',      label: 'GitLab',         component: IconBrandGitlab,    group: 'brand' },
  { key: 'figma',       label: 'Figma',          component: IconBrandFigma,     group: 'brand' },
  { key: 'linkedin',    label: 'LinkedIn',       component: IconBrandLinkedin,  group: 'brand' },
  { key: 'instagram',   label: 'Instagram',      component: IconBrandInstagram, group: 'brand' },
];

const ICON_MAP = Object.fromEntries(
  SIDEBAR_ICONS.map((i) => [i.key, i.component]),
) as Record<string, ComponentType<{ size?: number | string }>>;

export function getSidebarIcon(
  key: string | null | undefined,
): ComponentType<{ size?: number | string }> {
  if (key && ICON_MAP[key]) return ICON_MAP[key];
  return IconLink;
}
